import { HighlightedCourse } from '@/client/courses';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { useCourse } from '@/hooks/query/courses.hooks';
import { usePosthogTracking } from '@/hooks/usePosthogTracking';
import PagePlaceholderImage from '@/images/placeholders/course-placeholder.svg';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { CoursePlayIcon } from '@/utils/helpers';
import { PostHogCustomEvent } from '@/utils/posthog';
import classNames from 'classnames';
import moment from 'moment';
import { Card } from 'primereact/card';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledImageCard = styled(Card)`
  &.p-card {
    max-width: 400px;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--gray-dark);
    border-radius: var(--xsmall-border-radius);
    box-shadow: none;
    overflow: hidden;
    flex: 1;

    &:hover,
    &:focus-within {
      .p-card-header .image-card-overlay {
        opacity: 1;
      }
    }

    &.disabled {
      opacity: 0.6;
      pointer-events: none !important;
    }

    .p-card-body {
      display: flex;
      flex-direction: column;
      justify-content: center;
      flex: 1;
      padding: var(--small-padding);
      .p-card-title {
        margin-bottom: 0;
        font-size: var(--small-font-size);
        line-height: var(--small-line-height);
        font-weight: 500;
        color: var(--black-main);
      }
      .p-card-subtitle {
        margin-bottom: 0;
        font-size: var(--xsmall-font-size);
        line-height: var(--xsmall-line-height);
        color: var(--black-main);
      }
    }

    .p-card-header {
      aspect-ratio: 16 / 9;
      position: relative;
      display: flex;
      align-items: stretch;

      .image-card-thumbnail {
        background-size: contain;
        background-position: center center;
        background-repeat: no-repeat;
        aspect-ratio: 16 / 9;

        &.no-thumbnail {
          background-size: 63px 58px;
          background-position: center center;
          background-color: var(--blue-light);
        }
      }

      .image-card-overlay {
        position: absolute;
        height: 100%;
        padding: var(--small-padding);
        background: var(--orange-faint);
        opacity: 0;
        transition: all 0.4s;
      }
    }

    .p-card-footer {
      margin-top: auto;
    }
  }
`;

const StyledTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledAppButton = styled(AppButton)`
  &.p-button.p-button-icon-only {
    width: var(--big-button-height);
    height: var(--big-button-height);
    padding: 0;
    margin-left: var(--small-padding);
    border: 2px solid var(--gray-light) !important;

    .p-button-icon {
      font-size: var(--small-font-size);
    }

    &:hover {
      svg path {
        fill: var(--white-main);
      }

      background-color: var(--red-main) !important;
      border: 2px solid var(--red-main) !important;
    }

    @media screen and (max-width: 812px) {
      width: calc(1.2 * var(--small-button-height));
      height: calc(1.2 * var(--small-button-height));
    }
  }
`;

export type HighlightedCourseCardProps = {
  id: string;
  index: number;
  course: HighlightedCourse;
  isDisabled?: boolean;
  onClick?: () => void;
};

export const HighlightedCourseCard: React.FC<HighlightedCourseCardProps> = ({
  id,
  index,
  course,
  isDisabled,
  onClick,
  ...rest
}) => {
  const navigate = useNavigate();
  const { trackPostHogEvent } = usePosthogTracking();
  const { isLoading } = useCourse({
    courseId: id,
    staleTime: Infinity,
    enabled: false,
  });

  const className = classNames({
    disabled: isDisabled,
  });

  const header = (
    <label
      className={classNames('image-card-thumbnail flex-1', {
        'upcoming-course': moment(course.releaseDate).isAfter(moment(), 'day'),
        'no-thumbnail': !course.thumbnailSignedUrl,
      })}
      style={{
        backgroundImage: `url(${CSS.escape(
          course.thumbnailSignedUrl ?? PagePlaceholderImage,
        )}`,
      }}
    />
  );

  const titleField = (
    <FlexContainer justify="space-between" align="center">
      <StyledTitle>{course.name}</StyledTitle>
      <StyledAppButton
        dataId={`preview-btn-${index + 1}`}
        icon={<CoursePlayIcon />}
        type="outlined"
        state={isLoading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
        onClick={async () => {
          trackPostHogEvent(
            PostHogCustomEvent.USER_CLICKED_CONTENT_HIGHLIGHTED_CONTENT,
          );

          navigate(RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](id));
        }}
      />
    </FlexContainer>
  );

  return (
    <>
      <StyledImageCard
        id={id}
        title={titleField}
        className={className}
        header={header}
        onClick={onClick}
        {...rest}
      />
    </>
  );
};
